import type {ReactNode} from "react";
import {Dialog, DialogBody, DialogTitle} from "catalyst";
import {useAppDensity} from "../app/AppContext";
import {OtsBrand, OtsColorSchemePreference, OtsThemeRoot} from "../theme/OtsTheme";
import {XMarkIcon} from "@heroicons/react/20/solid";
import {OtsActionButton, OtsIconActionButton} from "./OtsButton";

export type OtsDialogSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl";

export type OtsDialogProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** A line under the title. Strings truncate to one line when {@link truncateDescription} is set. */
  description?: ReactNode;
  truncateDescription?: boolean;
  /** Header buttons placed before Close, e.g. a primary action. */
  actions?: ReactNode;
  closeLabel?: string;
  /** "text" shows a Close button; "icon" shows an ✕ named by {@link closeLabel}. */
  closeStyle?: "text" | "icon";
  /** Separates the header with a rule and sets the body on the page background, edge to edge. */
  divided?: boolean;
  size?: OtsDialogSize;
  /** Tighter chrome for embedded hosts. Defaults to the app density. */
  compact?: boolean;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
  bodyClassName?: string;
  children?: ReactNode;
};

/**
 * Themed dialog shell for inspectors and detail views: title, optional description and actions, a Close
 * button, and a body that scrolls within the viewport. It keeps WaterGo's shadow-root hosts themed by
 * wrapping both the portal and the panel in {@link OtsThemeRoot}.
 */
export const OtsDialog = ({
  open,
  onClose,
  title,
  description,
  truncateDescription = false,
  actions,
  closeLabel = "Close",
  closeStyle = "text",
  divided = false,
  size = "5xl",
  compact: compactProp,
  brand,
  colorScheme,
  bodyClassName,
  children,
}: OtsDialogProps) => {
  const density = useAppDensity();
  const compact = compactProp ?? density === "compact";
  return <OtsThemeRoot brand={brand} colorScheme={colorScheme} defaultColorScheme="system">
    <Dialog open={open} onClose={onClose} size={size} className="max-h-[calc(100vh-2rem)] overflow-y-auto !bg-transparent !p-0">
      <OtsThemeRoot brand={brand} colorScheme="inherit" data-density={compact ? "compact" : "default"}
        className={["rounded-t-3xl bg-[var(--ots-surface)] sm:rounded-2xl",
          divided ? "overflow-hidden" : compact ? "p-3 sm:p-4" : "p-4 sm:p-6"].join(" ")}>
        {/* `!flex`: a host reset (Mantine's) may override display outside any CSS layer. */}
        <div className={["!flex items-start justify-between gap-4",
          divided ? `border-b border-[var(--ots-outline-variant)] ${compact ? "px-4 py-3" : "px-5 py-4"}` : "px-1"].join(" ")}>
          <div className="min-w-0">
            <DialogTitle className={`!font-[family-name:var(--ots-font-display)] !font-semibold !text-[var(--ots-secondary)] ${compact || divided ? "!text-lg" : "!text-xl sm:!text-2xl"}`}>
              {title}
            </DialogTitle>
            {description != null && description !== "" &&
              <p className={`mt-1 text-[var(--ots-text-muted)] ${compact ? "text-xs" : "text-sm"} ${truncateDescription ? "truncate" : ""}`}
                title={truncateDescription && typeof description === "string" ? description : undefined}>
                {description}
              </p>}
          </div>
          <div className="!flex shrink-0 flex-wrap justify-end gap-2">
            {actions}
            {closeStyle === "icon"
              ? <OtsIconActionButton variant="text" size="compact" icon={<XMarkIcon/>} label={closeLabel} onClick={onClose}/>
              : <OtsActionButton size="compact" variant="text" onClick={onClose}>{closeLabel}</OtsActionButton>}
          </div>
        </div>
        <DialogBody className={["@container overflow-y-auto",
          divided ? `!mt-0 max-h-[70vh] bg-[var(--ots-background)] ${compact ? "p-4" : "p-5"}`
            : `max-h-[calc(100vh-9rem)] pr-1 ${compact ? "!mt-3" : "!mt-5"}`,
          bodyClassName].filter(Boolean).join(" ")}>
          {children}
        </DialogBody>
      </OtsThemeRoot>
    </Dialog>
  </OtsThemeRoot>;
};
